import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import api from "../lib/axios";

// Load read notification IDs from localStorage
const readFromLocal = JSON.parse(localStorage.getItem("read_notifications") || "[]");

export interface Notification {
  id: string;
  title: string | null;
  summary: string | null;
  description: string | null;
  created_at: string;
}

export interface Pagination {
    total: string | number;
    page: string | number;
    limit: string | number;
    totalPages: string | number;
}

interface NotificationState {
  items: Notification[];
  read: string[];
  pagination: Pagination | null;
  loading: boolean;
  unread: string | number;
}

const initialState: NotificationState = {
  items: [],
  read: readFromLocal,
  pagination: null,
  loading: false,
  unread: 0,
};

const buildNotificationUrl = (
    page: number, 
    limit: number, 
    search: string, 
    startDate: string, 
    endDate: string) => {
  const params = [
    page && `page=${page}`,
    limit && `limit=${limit}`,
    search && `search=${encodeURIComponent(search)}`,
    startDate && `startDate=${startDate}`,
    endDate && `endDate=${endDate}`
  ].filter(Boolean).join('&');
  
  return `/notification${params ? `?${params}` : ''}`;
};

// ------------------------------------------------------
// FETCH NOTIFICATIONS (PAGINATED)
// ------------------------------------------------------
export const fetchNotifications = createAsyncThunk(
  "notifications/fetch",
  async (
    { 
        page = 1, 
        limit = 10, 
        search="",
        startDate="",
        endDate="", }: 
    { 
        page?: number; 
        limit?: number; 
        search?: string;
        startDate?: string,
        endDate?: string; },
    { rejectWithValue }
  ) => {
    try {

      const res = await api.get(
        `${buildNotificationUrl(
            page,
            limit,
            search,
            startDate,
            endDate
        )}`
      );

      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to load notifications");
    }
  }
);

// ------------------------------------------------------
// MARK NOTIFICATION AS READ
// ------------------------------------------------------
export const markNotificationRead = createAsyncThunk(
  "notifications/markRead",
  async (notificationIds: string[]) => {
    // Load already-read notifications from storage
    const stored: string[] = JSON.parse(
      localStorage.getItem("read_notifications") || "[]"
    );

    // Merge + remove duplicates
    const merged = Array.from(new Set([...stored, ...notificationIds]));

    // Save updated list
    localStorage.setItem("read_notifications", JSON.stringify(merged));

    // OPTIONAL: sync with backend
    await api.post("/notification/read", { notificationIds });

    return merged; // return final read list
  }
);


const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchNotifications.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.items = action.payload.data;
          state.unread = action.payload.unread;
          state.pagination = action.payload.pagination;
        }
      )
      .addCase(fetchNotifications.rejected, (state) => {
        state.loading = false;
      })

      // MARK READ
      .addCase(markNotificationRead.fulfilled, (state, action) => {
            const readIds = action.payload; // ← merged list from thunk

            state.read = readIds; // Update the read array in state
            state.unread = state.items.filter(
                (n) => !readIds.includes(n.id)
            ).length;
        });

  },
});

export const notificationReducer = notificationSlice.reducer;
